import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"

interface Props {
  decisions: string[]
  language: "tr" | "en"
  onNext: (decision: string, otherDecisions: string[]) => void
}

export const Step3Select = ({ decisions, language, onNext }: Props) => {
  const [selected, setSelected] = useState<string>("")
  const [committed, setCommitted] = useState(false)
  const [showButton, setShowButton] = useState(false)
  const [showContextReset, setShowContextReset] = useState(false)

  const handleSelect = (decision: string) => {
    if (committed) return
    setSelected(decision)
    setTimeout(() => {
      setCommitted(true)
      setTimeout(() => setShowButton(true), 800)
    }, 600)
  }

  const handleContinue = () => {
    setShowContextReset(true)
  }

  const handleContextConfirm = () => {
    const others = decisions.filter(d => d !== selected)
    onNext(selected, others)
  }

  const t = language === "tr" ? {
    stepLabel: "Adım 3",
    title: "Bugün hangisine odaklanacaksın?",
    commitText: "Bugün bu.",
    commitSub: "Diğerleri bekleyecek.",
    continueBtn: "Devam Et",
    contextTitle: "Seçtin:",
    contextSub: "Şimdi sadece buna odaklanıyoruz.\nDiğer her şey bekleyecek.",
    contextBtn: "Devam Et",
  } : {
    stepLabel: "Step 3",
    title: "What will you focus on today?",
    commitText: "Today, this.",
    commitSub: "Others will wait.",
    continueBtn: "Continue",
    contextTitle: "You chose:",
    contextSub: "Now we focus only on this.\nEverything else will wait.",
    contextBtn: "Continue",
  }

  if (showContextReset) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        style={{ textAlign: "center" }}
      >
        <div className="step-label" style={{ marginBottom: "20px" }}>{t.contextTitle}</div>

        <div style={{
          padding: "18px 20px",
          background: "rgba(123,92,255,0.08)",
          border: "1px solid rgba(123,92,255,0.25)",
          borderRadius: "14px",
          marginBottom: "24px",
        }}>
          <div style={{ fontSize: "20px", fontWeight: "600", color: "var(--text-primary)", lineHeight: "1.4" }}>
            {selected}
          </div>
        </div>

        <div style={{
          fontSize: "15px",
          color: "var(--text-secondary)",
          lineHeight: "1.7",
          marginBottom: "32px",
          whiteSpace: "pre-line",
        }}>
          {t.contextSub}
        </div>

        <button onClick={handleContextConfirm} className="btn-primary">
          {t.contextBtn}
        </button>
      </motion.div>
    )
  }

  return (
    <div>
      <div className="step-label">{t.stepLabel}</div>
      <div className="step-title">{t.title}</div>

      <div>
        {decisions.map((decision) => {
          const isSelected = selected === decision
          const isOther = selected !== "" && !isSelected

          return (
            <motion.div
              key={decision}
              onClick={() => !committed && handleSelect(decision)}
              className={`decision-option${isSelected && committed ? " selected" : ""}`}
              animate={{
                opacity: isOther && committed ? 0 : 1,
                height: isOther && committed ? 0 : "auto",
                marginBottom: isOther && committed ? 0 : 8,
                paddingTop: isOther && committed ? 0 : 16,
                paddingBottom: isOther && committed ? 0 : 16,
              }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              style={{ overflow: "hidden", cursor: committed ? "default" : "pointer", pointerEvents: committed ? "none" : "auto" }}
            >
              {decision}
            </motion.div>
          )
        })}
      </div>

      <AnimatePresence>
        {committed && selected && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="commit-box">
              <div className="commit-text">{t.commitText}</div>
              <div className="commit-subtext">{t.commitSub}</div>
            </div>

            <AnimatePresence>
              {showButton && (
                <motion.button
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  onClick={handleContinue}
                  className="btn-primary"
                >
                  {t.continueBtn}
                </motion.button>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}